import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANG } from "@/i18n";
import { useLanguages } from "@/hooks/useLanguages";

interface ConversationTemplate {
  id: string;
  name: string;
  description: string;
  template_type: string;
  duration_months: number;
  tool_id: string;
}

interface ConversationTemplateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  template: ConversationTemplate | null;
  onSuccess: () => void;
}

export const ConversationTemplateDialog = ({
  isOpen,
  onClose,
  template,
  onSuccess,
}: ConversationTemplateDialogProps) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    template_type: "performance_review",
    duration_months: 12,
    tool_id: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { i18n } = useTranslation();
  const { data: languages } = useLanguages();
  const [activeLang, setActiveLang] = useState<string>(i18n.language || DEFAULT_LANG);
  const [nameI18n, setNameI18n] = useState<Record<string, string>>({});
  const [descI18n, setDescI18n] = useState<Record<string, string>>({});

  // Fetch tools for dropdown
  const { data: tools } = useQuery({
    queryKey: ['tools'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tools')
        .select('id, name')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || "",
        description: template.description || "",
        template_type: template.template_type || "performance_review",
        duration_months: template.duration_months || 12,
        tool_id: template.tool_id || "",
      });
      const t: any = template as any;
      setNameI18n((t.name_i18n as any) || {});
      setDescI18n((t.description_i18n as any) || {});
    } else {
      setFormData({
        name: "",
        description: "",
        template_type: "performance_review",
        duration_months: 12,
        tool_id: "",
      });
      setNameI18n({});
      setDescI18n({});
    }
  }, [template]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Template name is required");
      return;
    }

    if (!formData.tool_id) {
      toast.error("Tool selection is required");
      return;
    }

    setIsSubmitting(true);

  try {
      const submitData: any = {
        ...formData,
        name_i18n: nameI18n,
        description_i18n: descI18n,
      };
      if (template) {
        // Update existing template
        const { error } = await supabase
          .from('conversation_templates')
          .update(submitData)
          .eq('id', template.id);

        if (error) throw error;
        toast.success("Template updated successfully");
      } else {
        // Create new template
        const { error } = await supabase
          .from('conversation_templates')
          .insert([submitData]);

        if (error) throw error;
        toast.success("Template created successfully");
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error("Failed to save template");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {template ? "Edit Template" : "Create New Template"}
          </DialogTitle>
          <DialogDescription>
            {template
              ? "Update the conversation template details below."
              : "Create a new conversation template for performance reviews or development conversations."}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Template Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Annual Performance Review"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the purpose and flow of this conversation template..."
              rows={3}
            />
          </div>

          <div className="space-y-2 border rounded-md p-3">
            <div className="flex items-center justify-between">
              <Label>Translations</Label>
              <div className="w-48">
                <Select value={activeLang} onValueChange={(v) => setActiveLang(v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Language" />
                  </SelectTrigger>
                  <SelectContent>
                    {(languages || []).map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.native_name} ({l.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Localized Name ({activeLang})</Label>
                <Input
                  value={nameI18n[activeLang] || ''}
                  onChange={(e) => setNameI18n({ ...nameI18n, [activeLang]: e.target.value })}
                  placeholder="Translated template name"
                />
              </div>
              <div className="space-y-2">
                <Label>Localized Description ({activeLang})</Label>
                <Textarea
                  value={descI18n[activeLang] || ''}
                  onChange={(e) => setDescI18n({ ...descI18n, [activeLang]: e.target.value })}
                  placeholder="Translated description"
                  rows={3}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="template_type">Template Type</Label>
              <Select
                value={formData.template_type}
                onValueChange={(value) => setFormData({ ...formData, template_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="performance_review">Performance Review</SelectItem>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="exit_interview">Exit Interview</SelectItem>
                  <SelectItem value="wellbeing">Wellbeing Check</SelectItem>
                  <SelectItem value="career_development">Career Development</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="duration_months">Duration (Months)</Label>
              <Input
                id="duration_months"
                type="number"
                min="1"
                max="24"
                value={formData.duration_months}
                onChange={(e) => setFormData({ ...formData, duration_months: parseInt(e.target.value) || 12 })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tool_id">Associated Tool</Label>
            <Select
              value={formData.tool_id}
              onValueChange={(value) => setFormData({ ...formData, tool_id: value })}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a tool..." />
              </SelectTrigger>
              <SelectContent>
                {tools?.map((tool) => (
                  <SelectItem key={tool.id} value={tool.id}>
                    {tool.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : template ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};